import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  Music, Upload, Edit, Trash2, Plus, ArrowLeft, Save, X, Star, 
  Image, DollarSign, Clock, User, FileAudio, FolderOpen, Library,
  CheckCircle2, AlertTriangle, AlertCircle, Link2, UserPlus
} from 'lucide-react';
import { motion } from 'framer-motion';
import { adminFetch, isAdminAuthenticated } from '@/utils/adminAuth';
import { toast } from 'sonner';
import axios from 'axios';
import { MediaLibraryPicker } from '@/components/admin/MediaLibraryPicker';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminMusic = () => {
  const [tracks, setTracks] = useState([]);
  const [albums, setAlbums] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTrack, setEditingTrack] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('tracks'); // 'tracks' or 'albums'
  const [showAlbumModal, setShowAlbumModal] = useState(false);
  const [editingAlbum, setEditingAlbum] = useState(null);
  const [albumTrackSizes, setAlbumTrackSizes] = useState({}); // trackId -> {size, loading}
  const [trackPickerOpen, setTrackPickerOpen] = useState(false);
  const [pickerQuery, setPickerQuery] = useState('');
  const navigate = useNavigate();

  const emptyForm = {
    title: '',
    artist: 'Mestre Curtis Pierre',
    duration: 180,
    price: 1.99,
    audio_url: '',
    wav_url: '',
    wav_price: 2.99,
    cover_url: '',
    description: ''
  };

  const emptyAlbumForm = {
    title: '',
    artist: 'Mestre Curtis Pierre',
    price: 14.99,
    cover_url: '',
    description: '',
    zip_url: '',
    bestseller: false
  };

  const [formData, setFormData] = useState(emptyForm);
  const [albumFormData, setAlbumFormData] = useState(emptyAlbumForm);
  
  // Media Library Picker states
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  const [mediaPickerTarget, setMediaPickerTarget] = useState(null); // 'album_cover', 'album_zip', 'track_cover', 'track_audio', 'track_wav'

  useEffect(() => {
    if (!isAdminAuthenticated()) {
      navigate('/admin');
      return;
    }
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [tracksRes, albumsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/tracks`),
        axios.get(`${BACKEND_URL}/api/albums`)
      ]);
      setTracks(tracksRes.data);
      setAlbums(albumsRes.data);
    } catch (error) {
      toast.error('Failed to load music');
    } finally {
      setLoading(false);
    }
  };

  const handleAudioUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      setUploading(true);
      const response = await adminFetch('/admin/upload/audio', {
        method: 'POST',
        body: formDataUpload
      });
      const data = await response.json();
      setFormData(prev => ({ ...prev, audio_url: data.url }));
      toast.success('Audio uploaded!');
    } catch (error) {
      toast.error('Failed to upload audio');
    } finally {
      setUploading(false);
    }
  };

  const handleCoverUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      setUploading(true);
      const response = await adminFetch('/admin/upload/cover', {
        method: 'POST',
        body: formDataUpload
      });
      const data = await response.json();
      setFormData(prev => ({ ...prev, cover_url: data.url }));
      toast.success('Cover uploaded!');
    } catch (error) {
      toast.error('Failed to upload cover');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title) {
      toast.error('Title is required');
      return;
    }

    setSaving(true);
    try {
      if (editingTrack) {
        const response = await adminFetch(`/admin/tracks/${editingTrack.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData)
        });
        
        if (response.ok) {
          toast.success('Track updated!');
          closeModal();
          loadData();
        }
      } else {
        const response = await axios.post(`${BACKEND_URL}/api/tracks`, formData);
        if (response.status === 200) {
          toast.success('Track added!');
          closeModal();
          loadData();
        }
      }
    } catch (error) {
      toast.error('Failed to save track');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (trackId) => {
    if (!confirm('Are you sure you want to delete this track?')) return;

    try {
      const response = await adminFetch(`/admin/tracks/${trackId}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        toast.success('Track deleted!');
        loadData();
      }
    } catch (error) {
      toast.error('Failed to delete track');
    }
  };

  const toggleFeatured = async (trackId, currentFeatured) => {
    try {
      const response = await adminFetch(`/admin/track-feature/${trackId}`, {
        method: 'POST'
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.featured ? 'Added to Best Sellers!' : 'Removed from Best Sellers');
        loadData();
      }
    } catch (error) {
      toast.error('Failed to update featured status');
    }
  };

  const openEditModal = (track) => {
    setEditingTrack(track);
    setFormData({
      title: track.title || '',
      artist: track.artist || 'Mestre Curtis Pierre',
      duration: track.duration || 180,
      price: track.price || 1.99,
      audio_url: track.audio_url || '',
      wav_url: track.wav_url || '',
      wav_price: track.wav_price || 2.99,
      cover_url: track.cover_url || '',
      description: track.description || ''
    });
    setShowModal(true);
  };

  const openAddModal = () => {
    setEditingTrack(null);
    setFormData(emptyForm);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingTrack(null);
    setFormData(emptyForm);
  };

  // ============ ALBUM FUNCTIONS ============
  const handleAlbumSubmit = async (e) => {
    e.preventDefault();
    if (!albumFormData.title) {
      toast.error('Album title is required');
      return;
    }

    setSaving(true);
    try {
      if (editingAlbum) {
        const response = await adminFetch(`/admin/albums/${editingAlbum.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(albumFormData)
        });
        
        if (response.ok) {
          toast.success('Album updated!');
          closeAlbumModal();
          loadData();
        }
      } else {
        const response = await adminFetch('/admin/albums', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(albumFormData)
        });
        if (response.ok) {
          toast.success('Album added!');
          closeAlbumModal();
          loadData();
        }
      }
    } catch (error) {
      toast.error('Failed to save album');
    } finally {
      setSaving(false);
    }
  };

  const handleAlbumDelete = async (albumId) => {
    if (!confirm('Are you sure you want to delete this album? Tracks will NOT be deleted.')) return;

    try {
      const response = await adminFetch(`/admin/albums/${albumId}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        toast.success('Album deleted!');
        loadData();
      }
    } catch (error) {
      toast.error('Failed to delete album');
    }
  };

  const openAlbumEditModal = (album) => {
    setEditingAlbum(album);
    setAlbumFormData({
      title: album.title || '',
      artist: album.artist || 'Mestre Curtis Pierre',
      price: album.price || 14.99,
      cover_url: album.cover_url || '',
      description: album.description || '',
      zip_url: album.zip_url || '',
      bestseller: album.bestseller || false
    });
    setShowAlbumModal(true);
  };

  const openAlbumAddModal = () => {
    setEditingAlbum(null);
    setAlbumFormData(emptyAlbumForm);
    setShowAlbumModal(true);
  };

  const closeAlbumModal = () => {
    setShowAlbumModal(false);
    setEditingAlbum(null);
    setAlbumFormData(emptyAlbumForm);
    setAlbumTrackSizes({});
    setTrackPickerOpen(false);
    setPickerQuery('');
  };

  // --- Album track-list management ---
  // Tracks currently linked to the album being edited (via album_id)
  const albumTracks = useMemo(() => {
    if (!editingAlbum) return [];
    return tracks.filter(t => t.album_id === editingAlbum.id);
  }, [tracks, editingAlbum]);

  // Tracks that are NOT in this album, filterable by search
  const availableTracks = useMemo(() => {
    if (!editingAlbum) return [];
    const q = pickerQuery.trim().toLowerCase();
    return tracks
      .filter(t => t.album_id !== editingAlbum.id)
      .filter(t => !q || (t.title || '').toLowerCase().includes(q) || (t.artist || '').toLowerCase().includes(q));
  }, [tracks, editingAlbum, pickerQuery]);

  // HEAD-check each track's audio file to classify status (full/sample/missing)
  // when the modal opens, so a status badge can be shown next to each row.
  const probeTrackSize = async (track) => {
    if (albumTrackSizes[track.id] !== undefined) return;
    setAlbumTrackSizes(p => ({ ...p, [track.id]: { loading: true } }));
    let url = track.audio_url || track.file_url || '';
    if (!url) {
      setAlbumTrackSizes(p => ({ ...p, [track.id]: { size: 0, status: 'missing' } }));
      return;
    }
    if (url.startsWith('/')) url = `${BACKEND_URL}${url}`;
    try {
      const r = await fetch(url, { method: 'HEAD' });
      if (!r.ok) {
        setAlbumTrackSizes(p => ({ ...p, [track.id]: { status: 'missing', http: r.status } }));
        return;
      }
      const cl = parseInt(r.headers.get('content-length') || '0', 10);
      const status = cl < 500_000 ? 'sample' : 'full';
      setAlbumTrackSizes(p => ({ ...p, [track.id]: { size: cl, status } }));
    } catch (e) {
      setAlbumTrackSizes(p => ({ ...p, [track.id]: { status: 'missing', error: e.message } }));
    }
  };

  useEffect(() => {
    if (!showAlbumModal || !editingAlbum) return;
    albumTracks.forEach(t => probeTrackSize(t));
  }, [showAlbumModal, editingAlbum, albumTracks]); // eslint-disable-line

  const assignTrackToAlbum = async (trackId, albumId) => {
    try {
      const t = tracks.find(x => x.id === trackId);
      if (!t) return;
      // Reuse the existing update endpoint — it takes TrackCreate shape
      const payload = { ...t, album_id: albumId || null };
      delete payload._id;
      delete payload.id;
      const r = await adminFetch(`/admin/tracks/${trackId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      // Optimistically update local state
      setTracks(prev => prev.map(x => x.id === trackId ? { ...x, album_id: albumId || null } : x));
      toast.success(albumId ? `Added "${t.title}" to album` : `Removed "${t.title}" from album`);
    } catch (e) {
      toast.error(e.message || 'Failed to update track');
    }
  };

  const handleAlbumCoverUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      setUploading(true);
      const response = await adminFetch('/admin/upload/image', {
        method: 'POST',
        body: formDataUpload
      });
      const data = await response.json();
      setAlbumFormData(prev => ({ ...prev, cover_url: data.url }));
      toast.success('Cover image uploaded!');
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  // Media Library selection handler
  const openMediaPicker = (target) => {
    setMediaPickerTarget(target);
    setShowMediaPicker(true);
  };

  const handleMediaSelect = (selectedItems) => {
    // Accept either a single media object (new API) or an array (legacy).
    const list = Array.isArray(selectedItems) ? selectedItems : (selectedItems ? [selectedItems] : []);
    if (list.length === 0) return;
    const item = list[0];
    const fullUrl = item.url.startsWith('http') ? item.url : `${BACKEND_URL}${item.url}`;
    
    switch (mediaPickerTarget) {
      case 'album_cover':
        setAlbumFormData(prev => ({ ...prev, cover_url: fullUrl }));
        break;
      case 'album_zip':
        setAlbumFormData(prev => ({ ...prev, zip_url: fullUrl }));
        break;
      case 'track_cover':
        setFormData(prev => ({ ...prev, cover_url: fullUrl }));
        break;
      case 'track_audio':
        setFormData(prev => ({ ...prev, audio_url: fullUrl }));
        break;
      case 'track_wav':
        setFormData(prev => ({ ...prev, wav_url: fullUrl }));
        break;
      default:
        break;
    }
    
    toast.success('Media selected!');
    setShowMediaPicker(false);
    setMediaPickerTarget(null);
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-900 flex items-center justify-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
        >
          <Music className="w-12 h-12 text-primary" />
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-900 text-white">
      {/* Header */}
      <div className="bg-stone-800 border-b border-stone-700 px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin/dashboard" className="p-2 hover:bg-stone-700 rounded-lg transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-2xl font-bold">Manage Music</h1>
              <p className="text-sm text-muted-foreground">{tracks.length} tracks in your library</p>
            </div>
          </div>
          <div className="flex gap-3">
            <Link
              to="/admin/music/bulk-wav"
              className="px-4 py-2 bg-purple-600 rounded-lg hover:bg-purple-500 transition-colors text-sm font-medium flex items-center gap-2"
            >
              <Upload className="w-4 h-4" />
              Bulk WAV Upload
            </Link>
            <Link
              to="/music"
              target="_blank"
              className="px-4 py-2 bg-stone-700 rounded-lg hover:bg-stone-600 transition-colors text-sm"
            >
              View Store
            </Link>
            <button
              onClick={openAddModal}
              className="px-4 py-2 bg-primary text-black font-bold rounded-lg hover:bg-primary/90 transition-colors flex items-center gap-2"
              data-testid="add-track-btn"
            >
              <Plus className="w-4 h-4" />
              Add Track
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-stone-800 rounded-xl p-4 border border-stone-700">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary/20 rounded-lg flex items-center justify-center">
                <Music className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{tracks.length}</p>
                <p className="text-xs text-muted-foreground">Total Tracks</p>
              </div>
            </div>
          </div>
          <div className="bg-stone-800 rounded-xl p-4 border border-stone-700">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                <FolderOpen className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{albums.length}</p>
                <p className="text-xs text-muted-foreground">Albums</p>
              </div>
            </div>
          </div>
          <div className="bg-stone-800 rounded-xl p-4 border border-stone-700">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-yellow-500/20 rounded-lg flex items-center justify-center">
                <Star className="w-5 h-5 text-yellow-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{tracks.filter(t => t.featured).length}</p>
                <p className="text-xs text-muted-foreground">Best Sellers</p>
              </div>
            </div>
          </div>
          <div className="bg-stone-800 rounded-xl p-4 border border-stone-700">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">${tracks.reduce((sum, t) => sum + (t.price || 0), 0).toFixed(0)}</p>
                <p className="text-xs text-muted-foreground">Catalog Value</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab('tracks')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'tracks' 
                ? 'bg-primary text-black' 
                : 'bg-stone-800 text-muted-foreground hover:bg-stone-700'
            }`}
          >
            <Music className="w-4 h-4 inline mr-2" />
            Tracks ({tracks.length})
          </button>
          <button
            onClick={() => setActiveTab('albums')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'albums' 
                ? 'bg-purple-500 text-white' 
                : 'bg-stone-800 text-muted-foreground hover:bg-stone-700'
            }`}
          >
            <FolderOpen className="w-4 h-4 inline mr-2" />
            Albums ({albums.length})
          </button>
        </div>

        {/* Albums Section */}
        {activeTab === 'albums' && (
          <>
            {/* Duplicate album banner — only shows when 2+ albums share a title */}
            {(() => {
              const titleGroups = {};
              albums.forEach(a => {
                const key = (a.title || '').trim().toLowerCase();
                if (!key) return;
                titleGroups[key] = (titleGroups[key] || 0) + 1;
              });
              const dupeTitles = Object.entries(titleGroups).filter(([, n]) => n >= 2).map(([t]) => t);
              if (dupeTitles.length === 0) return null;
              return (
                <div className="mb-4 rounded-xl border border-amber-500/40 bg-amber-500/10 p-4 flex items-center justify-between gap-4">
                  <div className="text-sm text-amber-200">
                    <strong>Duplicate albums detected</strong> ({dupeTitles.length} title{dupeTitles.length === 1 ? '' : 's'}). The richest copy will be kept; orphaned tracks are not deleted — their album link is simply cleared so you can re-assign them.
                  </div>
                  <button
                    data-testid="dedupe-albums-btn"
                    onClick={async () => {
                      if (!window.confirm('Merge duplicate albums? The copy with more tracks + metadata (bestseller, zip) is kept. Others are deleted. Orphaned tracks remain and just lose their album_id so nothing is lost.')) return;
                      try {
                        const r = await adminFetch('/admin/track-repair/dedupe-albums', { method: 'POST' });
                        const data = await r.json();
                        toast.success(`Merged ${data.summary.groups_merged} duplicate group(s).`);
                        loadData();
                      } catch (err) {
                        toast.error(err.message || 'Dedupe failed');
                      }
                    }}
                    className="px-4 py-2 bg-amber-500 hover:bg-amber-400 text-black font-bold rounded-lg text-sm whitespace-nowrap"
                  >
                    Merge duplicates
                  </button>
                </div>
              );
            })()}
            <div className="flex justify-between items-center mb-4">
              <p className="text-muted-foreground">Manage your album collection</p>
              <button
                onClick={openAlbumAddModal}
                className="px-4 py-2 bg-purple-500 text-white font-bold rounded-lg hover:bg-purple-400 transition-colors flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Add Album
              </button>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {albums.map((album) => (
                <motion.div
                  key={album.id}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="bg-stone-800 rounded-xl border border-stone-700 overflow-hidden hover:border-purple-500/50 transition-colors"
                >
                  <div className="aspect-square relative">
                    {album.cover_url ? (
                      <img loading="lazy" decoding="async" 
                        src={album.cover_url} 
                        alt={album.title}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.src = 'https://via.placeholder.com/300x300?text=No+Cover';
                        }}
                      />
                    ) : (
                      <div className="w-full h-full bg-stone-700 flex items-center justify-center">
                        <FolderOpen className="w-16 h-16 text-stone-500" />
                      </div>
                    )}
                    <div className="absolute top-2 right-2 flex gap-1">
                      <button
                        onClick={() => openAlbumEditModal(album)}
                        className="p-2 bg-stone-900/80 rounded-lg hover:bg-stone-900 transition-colors"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleAlbumDelete(album.id)}
                        className="p-2 bg-red-500/80 rounded-lg hover:bg-red-500 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="font-bold text-lg truncate">{album.title}</h3>
                    <p className="text-muted-foreground text-sm truncate">{album.artist}</p>
                    <div className="flex items-center justify-between mt-3">
                      <span className="text-primary font-bold">${album.price?.toFixed(2) || '0.00'}</span>
                      {(() => {
                        const linked = tracks.filter(t => t.album_id === album.id).length;
                        const expected = album.track_count;
                        const mismatch = typeof expected === 'number' && expected !== linked;
                        return (
                          <span
                            data-testid={`album-track-count-${album.id}`}
                            className={`text-xs ${mismatch ? 'text-amber-400 font-semibold' : 'text-stone-500'}`}
                            title={mismatch ? `This album says it should have ${expected} tracks but only ${linked} are linked. Open Edit to add missing tracks.` : ''}
                          >
                            {linked} tracks
                            {mismatch ? ` ⚠ expected ${expected}` : ''}
                          </span>
                        );
                      })()}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </>
        )}

        {/* Tracks Section */}
        {activeTab === 'tracks' && (
          <>
        {/* Legend */}
        <div className="flex items-center gap-4 mb-4 text-sm text-muted-foreground">
          <div className="flex items-center gap-1">
            <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
            <span>= Best Seller</span>
          </div>
          <span>Click <Edit className="w-4 h-4 inline" /> to edit in place</span>
        </div>

        {/* Tracks List */}
        <div className="space-y-3">
          {tracks.map((track, index) => (
            <motion.div
              key={track.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.02 }}
              className="bg-stone-800 rounded-xl border border-stone-700 overflow-hidden hover:border-stone-600 transition-colors"
            >
              <div className="flex items-center gap-4 p-4">
                {/* Cover Image */}
                <div className="w-16 h-16 rounded-lg overflow-hidden bg-stone-700 flex-shrink-0">
                  {track.cover_url ? (
                    <img loading="lazy" decoding="async" 
                      src={track.cover_url} 
                      alt={track.title}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.src = 'https://via.placeholder.com/64x64?text=No+Cover';
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Music className="w-6 h-6 text-stone-500" />
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-bold text-lg truncate">{track.title}</h3>
                    {track.featured && (
                      <span className="px-2 py-0.5 bg-yellow-500/20 text-yellow-400 text-xs font-bold rounded-full flex-shrink-0">
                        Best Seller
                      </span>
                    )}
                  </div>
                  <p className="text-muted-foreground text-sm truncate">{track.artist}</p>
                  <div className="flex items-center gap-3 mt-1 text-xs text-stone-500">
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {formatDuration(track.duration || 0)}
                    </span>
                    {track.audio_url && (
                      <span className="flex items-center gap-1 text-green-500">
                        <FileAudio className="w-3 h-3" />
                        Audio
                      </span>
                    )}
                  </div>
                </div>

                {/* Price */}
                <div className="text-right flex-shrink-0">
                  <div className="text-xl font-bold text-primary">${(track.price || 0).toFixed(2)}</div>
                </div>

                {/* Actions */}
                <div className="flex gap-2 flex-shrink-0">
                  <button
                    onClick={() => toggleFeatured(track.id, track.featured)}
                    className={`p-2 rounded-lg transition-colors ${
                      track.featured 
                        ? 'bg-yellow-500/20 text-yellow-400' 
                        : 'bg-stone-700 text-stone-400 hover:text-yellow-400'
                    }`}
                    title={track.featured ? 'Remove from Best Sellers' : 'Add to Best Sellers'}
                    data-testid={`toggle-featured-${track.id}`}
                  >
                    <Star className={`w-5 h-5 ${track.featured ? 'fill-yellow-400' : ''}`} />
                  </button>
                  <button
                    onClick={() => openEditModal(track)}
                    className="p-2 bg-stone-700 rounded-lg hover:bg-primary/20 hover:text-primary transition-colors"
                    title="Edit track"
                    data-testid={`edit-track-${track.id}`}
                  >
                    <Edit className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleDelete(track.id)}
                    className="p-2 bg-stone-700 rounded-lg hover:bg-red-500/20 hover:text-red-400 transition-colors"
                    title="Delete track"
                    data-testid={`delete-track-${track.id}`}
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </motion.div>
          ))}

          {tracks.length === 0 && (
            <div className="bg-stone-800 rounded-xl border border-stone-700 p-12 text-center">
              <Music className="w-16 h-16 mx-auto mb-4 text-stone-600" />
              <h3 className="text-xl font-bold mb-2">No Tracks Yet</h3>
              <p className="text-muted-foreground mb-4">Add your first track to get started</p>
              <button
                onClick={openAddModal}
                className="px-4 py-2 bg-primary text-black font-bold rounded-lg hover:bg-primary/90 transition-colors"
              >
                Add Track
              </button>
            </div>
          )}
        </div>
          </>
        )}
      </div>

      {/* Album Edit/Add Modal */}
      {showAlbumModal && (
        <div 
          className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
          onClick={closeAlbumModal}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-stone-800 rounded-2xl p-6 max-w-lg w-full border border-stone-700"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold">
                {editingAlbum ? `Edit: ${editingAlbum.title}` : 'Add New Album'}
              </h3>
              <button onClick={closeAlbumModal} className="p-1 hover:bg-stone-700 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleAlbumSubmit} className="space-y-4">
              {/* ---- Tracks in this album (shown FIRST so it's immediately visible) ---- */}
              {editingAlbum && (
                <div className="rounded-xl bg-blue-500/5 border border-blue-500/30 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <div className="text-sm font-bold text-white flex items-center gap-2">
                        <FileAudio className="w-4 h-4 text-blue-400" /> Tracks in this album
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        <strong className="text-white">{albumTracks.length}</strong> track{albumTracks.length === 1 ? '' : 's'} currently linked
                        {typeof editingAlbum.track_count === 'number' && editingAlbum.track_count !== albumTracks.length ? (
                          <span className="text-amber-400"> · expected {editingAlbum.track_count}</span>
                        ) : null}
                      </p>
                    </div>
                    <button
                      type="button"
                      data-testid="open-track-picker"
                      onClick={() => setTrackPickerOpen(v => !v)}
                      className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-600 hover:bg-blue-500 rounded-lg text-xs font-semibold"
                    >
                      <Plus className="w-3.5 h-3.5" /> Add track
                    </button>
                  </div>

                  {albumTracks.length === 0 ? (
                    <div className="rounded-lg bg-stone-900/60 border border-dashed border-stone-700 p-3 text-sm text-muted-foreground">
                      No tracks assigned yet. Click <strong>Add track</strong>.
                    </div>
                  ) : (
                    <div className="rounded-lg border border-stone-700 bg-stone-900/60 divide-y divide-stone-800 max-h-48 overflow-y-auto">
                      {albumTracks.map((t) => {
                        const probe = albumTrackSizes[t.id];
                        const status = probe?.status;
                        return (
                          <div key={t.id} className="flex items-center gap-2 px-3 py-2 text-xs">
                            {status === 'full' ? (
                              <span className="inline-flex items-center gap-1 text-emerald-400" title="Full file"><CheckCircle2 className="w-3.5 h-3.5" />Full</span>
                            ) : status === 'sample' ? (
                              <span className="inline-flex items-center gap-1 text-amber-400" title="30s sample"><AlertTriangle className="w-3.5 h-3.5" />Sample</span>
                            ) : status === 'missing' ? (
                              <span className="inline-flex items-center gap-1 text-rose-400" title="Unreachable"><AlertCircle className="w-3.5 h-3.5" />Missing</span>
                            ) : (
                              <span className="text-stone-500 w-14">checking…</span>
                            )}
                            <span className="flex-1 truncate text-white">{t.title}</span>
                            {probe?.size ? (
                              <span className="text-stone-500 whitespace-nowrap">{Math.round(probe.size / 1024)} KB</span>
                            ) : null}
                            <button
                              type="button"
                              data-testid={`remove-from-album-${t.id}`}
                              onClick={() => assignTrackToAlbum(t.id, null)}
                              title="Remove from album"
                              className="p-1 rounded hover:bg-rose-500/20 text-rose-400"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {trackPickerOpen && (
                    <div className="mt-3 rounded-lg border border-blue-500/40 bg-stone-900/80 p-3">
                      <input
                        type="text"
                        placeholder="Search tracks by title or artist…"
                        value={pickerQuery}
                        onChange={(e) => setPickerQuery(e.target.value)}
                        className="w-full px-3 py-2 bg-stone-900 border border-stone-700 rounded text-sm mb-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <div className="max-h-48 overflow-y-auto divide-y divide-stone-800">
                        {availableTracks.length === 0 ? (
                          <p className="text-xs text-muted-foreground py-2">No tracks match.</p>
                        ) : availableTracks.slice(0, 50).map((t) => (
                          <button
                            type="button"
                            key={t.id}
                            data-testid={`add-track-to-album-${t.id}`}
                            onClick={() => assignTrackToAlbum(t.id, editingAlbum.id)}
                            className="w-full flex items-center justify-between gap-2 px-2 py-2 text-xs text-left hover:bg-stone-800 rounded"
                          >
                            <span className="truncate">
                              <span className="text-white">{t.title}</span>
                              <span className="text-stone-500 ml-2">{t.artist}</span>
                            </span>
                            <span className="text-blue-400 whitespace-nowrap inline-flex items-center gap-1">
                              <Link2 className="w-3 h-3" /> link
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Cover Image */}
              <div>
                <label className="block text-sm font-medium mb-2">Album Cover</label>
                <div className="flex items-start gap-4">
                  <div className="w-24 h-24 rounded-lg overflow-hidden bg-stone-700 flex-shrink-0">
                    {albumFormData.cover_url ? (
                      <img loading="lazy" decoding="async" 
                        src={albumFormData.cover_url} 
                        alt="Cover" 
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <FolderOpen className="w-8 h-8 text-stone-500" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 space-y-2">
                    <button
                      type="button"
                      onClick={() => openMediaPicker('album_cover')}
                      className="w-full px-4 py-2 bg-purple-500 hover:bg-purple-400 text-white rounded-lg font-medium flex items-center justify-center gap-2"
                    >
                      <Library className="w-4 h-4" />
                      Browse Media Library
                    </button>
                    <p className="text-xs text-muted-foreground text-center">Or paste URL:</p>
                    <input
                      type="text"
                      value={albumFormData.cover_url}
                      onChange={(e) => setAlbumFormData(prev => ({ ...prev, cover_url: e.target.value }))}
                      placeholder="https://..."
                      className="w-full px-3 py-2 bg-stone-900 border border-stone-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                </div>
              </div>

              {/* Title */}
              <div>
                <label className="block text-sm font-medium mb-2">Album Title *</label>
                <input
                  type="text"
                  value={albumFormData.title}
                  onChange={(e) => setAlbumFormData(prev => ({ ...prev, title: e.target.value }))}
                  required
                  className="w-full px-4 py-3 bg-stone-900 border border-stone-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>

              {/* Artist */}
              <div>
                <label className="block text-sm font-medium mb-2">Artist</label>
                <input
                  type="text"
                  value={albumFormData.artist}
                  onChange={(e) => setAlbumFormData(prev => ({ ...prev, artist: e.target.value }))}
                  className="w-full px-4 py-3 bg-stone-900 border border-stone-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>

              {/* Price */}
              <div>
                <label className="block text-sm font-medium mb-2">Price ($)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={albumFormData.price}
                  onChange={(e) => setAlbumFormData(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))}
                  className="w-full px-4 py-3 bg-stone-900 border border-stone-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium mb-2">Description</label>
                <textarea
                  value={albumFormData.description}
                  onChange={(e) => setAlbumFormData(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  className="w-full px-4 py-3 bg-stone-900 border border-stone-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="Album description..."
                />
              </div>

              {/* ZIP Download File */}
              <div>
                <label className="block text-sm font-medium mb-2">Album ZIP File (for downloads)</label>
                <div className="space-y-2">
                  <button
                    type="button"
                    onClick={() => openMediaPicker('album_zip')}
                    className="w-full px-4 py-2 bg-green-600 hover:bg-green-500 text-white rounded-lg font-medium flex items-center justify-center gap-2"
                  >
                    <Library className="w-4 h-4" />
                    Choose ZIP from Media Library
                  </button>
                  {albumFormData.zip_url && (
                    <div className="flex items-center gap-2 p-2 bg-stone-900 rounded-lg border border-stone-700">
                      <span className="text-xs text-green-400 flex-1 truncate">{albumFormData.zip_url}</span>
                      <button
                        type="button"
                        onClick={() => setAlbumFormData(prev => ({ ...prev, zip_url: '' }))}
                        className="p-1 hover:bg-stone-700 rounded text-red-400"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground">Or paste URL:</p>
                  <input
                    type="text"
                    value={albumFormData.zip_url}
                    onChange={(e) => setAlbumFormData(prev => ({ ...prev, zip_url: e.target.value }))}
                    placeholder="https://... (ZIP file URL)"
                    className="w-full px-3 py-2 bg-stone-900 border border-stone-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>
              </div>

              {/* Bestseller Toggle */}
              <div className="flex items-center gap-3 p-4 bg-gradient-to-r from-orange-500/10 to-red-500/10 border border-orange-500/30 rounded-lg">
                <input
                  type="checkbox"
                  id="bestseller"
                  checked={albumFormData.bestseller}
                  onChange={(e) => setAlbumFormData(prev => ({ ...prev, bestseller: e.target.checked }))}
                  className="w-5 h-5 rounded border-orange-500 text-orange-500 focus:ring-orange-500"
                />
                <label htmlFor="bestseller" className="flex-1">
                  <span className="font-bold text-orange-400">⭐ Mark as Bestseller</span>
                  <p className="text-xs text-muted-foreground">Highlighted prominently in the store</p>
                </label>
              </div>

              {/* Submit */}
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={closeAlbumModal}
                  className="px-4 py-2 text-muted-foreground hover:text-white transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-6 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-400 transition-colors flex items-center gap-2 font-medium disabled:opacity-50"
                >
                  {saving ? (
                    <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity }}>
                      <Upload className="w-4 h-4" />
                    </motion.div>
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  {editingAlbum ? 'Update Album' : 'Create Album'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {/* Edit/Add Modal - Opens in place, doesn't scroll */}
      {showModal && (
        <div 
          className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
          onClick={closeModal}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-stone-800 rounded-2xl p-6 max-w-2xl w-full border border-stone-700 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold">
                {editingTrack ? `Edit: ${editingTrack.title}` : 'Add New Track'}
              </h3>
              <button onClick={closeModal} className="p-1 hover:bg-stone-700 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Cover Preview */}
              <div>
                <label className="block text-sm font-medium mb-2">Cover Image</label>
                <div className="flex items-start gap-4">
                  <div className="w-24 h-24 rounded-lg overflow-hidden bg-stone-700 flex-shrink-0">
                    {formData.cover_url ? (
                      <img loading="lazy" decoding="async" 
                        src={formData.cover_url} 
                        alt="Cover" 
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Image className="w-8 h-8 text-stone-500" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 space-y-2">
                    <button
                      type="button"
                      onClick={() => openMediaPicker('track_cover')}
                      className="w-full px-4 py-2 bg-primary hover:bg-primary/80 text-black rounded-lg font-medium flex items-center justify-center gap-2"
                    >
                      <Library className="w-4 h-4" />
                      Browse Media Library
                    </button>
                    <p className="text-xs text-muted-foreground text-center">Or paste URL:</p>
                    <input
                      type="text"
                      value={formData.cover_url}
                      onChange={(e) => setFormData(prev => ({ ...prev, cover_url: e.target.value }))}
                      placeholder="https://..."
                      className="w-full px-3 py-2 bg-stone-900 border border-stone-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                </div>
              </div>

              {/* Title & Artist */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Title *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    required
                    className="w-full px-4 py-3 bg-stone-900 border border-stone-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Artist</label>
                  <input
                    type="text"
                    value={formData.artist}
                    onChange={(e) => setFormData(prev => ({ ...prev, artist: e.target.value }))}
                    className="w-full px-4 py-3 bg-stone-900 border border-stone-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
              </div>

              {/* Duration & Price */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Duration (sec)</label>
                  <input
                    type="number"
                    value={formData.duration}
                    onChange={(e) => setFormData(prev => ({ ...prev, duration: parseInt(e.target.value) || 0 }))}
                    className="w-full px-4 py-3 bg-stone-900 border border-stone-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">MP3 Price ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))}
                    className="w-full px-4 py-3 bg-stone-900 border border-stone-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">WAV Price ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.wav_price}
                    onChange={(e) => setFormData(prev => ({ ...prev, wav_price: parseFloat(e.target.value) || 0 }))}
                    className="w-full px-4 py-3 bg-stone-900 border border-stone-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="2.99"
                  />
                </div>
              </div>

              {/* MP3 Audio File */}
              <div>
                <label className="block text-sm font-medium mb-2">MP3 File</label>
                <button
                  type="button"
                  onClick={() => openMediaPicker('track_audio')}
                  className="w-full px-4 py-2 bg-green-600 hover:bg-green-500 text-white rounded-lg font-medium flex items-center justify-center gap-2 mb-2"
                >
                  <Library className="w-4 h-4" />
                  Browse Audio Library
                </button>
                <p className="text-xs text-muted-foreground">Or paste URL:</p>
                <input
                  type="text"
                  value={formData.audio_url}
                  onChange={(e) => setFormData(prev => ({ ...prev, audio_url: e.target.value }))}
                  placeholder="https://..."
                  className="w-full mt-1 px-3 py-2 bg-stone-900 border border-stone-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
                {formData.audio_url && (
                  <p className="text-xs text-green-500 mt-1 flex items-center gap-1">
                    <FileAudio className="w-3 h-3" />
                    MP3 file set
                  </p>
                )}
              </div>

              {/* WAV Audio File */}
              <div>
                <label className="block text-sm font-medium mb-2">WAV File (Lossless - Higher Price)</label>
                <button
                  type="button"
                  onClick={() => openMediaPicker('track_wav')}
                  className="w-full px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-lg font-medium flex items-center justify-center gap-2 mb-2"
                >
                  <Library className="w-4 h-4" />
                  Browse WAV Library
                </button>
                <p className="text-xs text-muted-foreground">Or paste URL:</p>
                <input
                  type="text"
                  value={formData.wav_url}
                  onChange={(e) => setFormData(prev => ({ ...prev, wav_url: e.target.value }))}
                  placeholder="https://..."
                  className="w-full mt-1 px-3 py-2 bg-stone-900 border border-stone-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
                {formData.wav_url && (
                  <p className="text-xs text-purple-400 mt-1 flex items-center gap-1">
                    <FileAudio className="w-3 h-3" />
                    WAV file set (Lossless)
                  </p>
                )}
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium mb-2">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  rows={2}
                  className="w-full px-4 py-3 bg-stone-900 border border-stone-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                />
              </div>

              {/* Buttons */}
              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  disabled={saving || uploading}
                  className="flex-1 px-6 py-3 bg-primary text-black font-bold rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {saving ? (
                    <>
                      <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      {editingTrack ? 'Update Track' : 'Add Track'}
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-6 py-3 bg-stone-700 rounded-lg hover:bg-stone-600 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {/* Media Library Picker Modal */}
      {showMediaPicker && (
        <MediaLibraryPicker
          isOpen={showMediaPicker}
          onClose={() => {
            setShowMediaPicker(false);
            setMediaPickerTarget(null);
          }}
          onSelect={handleMediaSelect}
          multiple={false}
          allowedTypes={
            mediaPickerTarget === 'album_zip' 
              ? ['archive'] 
              : mediaPickerTarget?.includes('audio') || mediaPickerTarget?.includes('wav') 
                ? ['audio'] 
                : ['image']
          }
        />
      )}
    </div>
  );
};

export default AdminMusic;
